import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { RotateCcw, RotateCw, X } from 'lucide-react';
import { useRotatePhoto } from '../../stores/asset-list';
import type { AssetPhoto } from '../../types/api';

interface PhotoEditOverlayProps {
  photo: AssetPhoto;
  onClose: () => void;
}

export function PhotoEditOverlay({ photo, onClose }: PhotoEditOverlayProps) {
  const { mutate: rotate } = useRotatePhoto();
  const [localRotation, setLocalRotation] = useState(photo.rotation_pending || 0);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) onClose();
  }

  function handleRotate(direction: 'cw' | 'ccw') {
    setLocalRotation((prev) => prev + (direction === 'cw' ? 90 : -90));
    rotate({ photoId: photo.photo_id, direction });
  }

  const isRightAngle = Math.abs(localRotation % 180) === 90;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/80">
      <div
        className="flex min-h-0 flex-1 items-center justify-center"
        onClick={handleBackdropClick}
      >
        <img
          src={photo.photo_url}
          alt={photo.filename || 'Asset photo'}
          className="object-contain transition-transform duration-200"
          style={{
            maxHeight: isRightAngle ? '85vw' : 'calc(100vh - 5rem)',
            maxWidth: isRightAngle ? 'calc(100vh - 5rem)' : '85vw',
            transform: `rotate(${localRotation}deg)`,
          }}
        />
      </div>
      <div className="flex shrink-0 justify-center gap-2 pb-4">
        <Button variant="secondary" size="sm" onClick={() => handleRotate('ccw')}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Rotate CCW
        </Button>
        <Button variant="secondary" size="sm" onClick={() => handleRotate('cw')}>
          <RotateCw className="mr-2 h-4 w-4" />
          Rotate CW
        </Button>
        <Button variant="secondary" size="sm" onClick={onClose}>
          <X className="mr-2 h-4 w-4" />
          Close
        </Button>
      </div>
    </div>
  );
}
